// ===================================
// ENHANCED PUBLIC CHAT PAGE WITH AGENTIC AUTH - src/pages/WidgetChat.tsx
// ===================================
import type React from "react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PublicChat from "../components/PublicChat";
import PublicChatWidget from "../components/PublicChatWidget";
import { publicChatApi, agenticAuthApi, sessionManager } from "../services/api";

import {
  MessageSquare,
  Shield,
  Zap,
  BookOpen,
  Users,
  Phone,
  Mail,
  ExternalLink,
  User,
  LogIn,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Menu,
  AlertTriangle,
  Key,
  Lock,
  CheckCircle,
  Bot,
} from "lucide-react";
import AiChatIcon from "../assets/aiChatIcon.png";

const WidgetChat: React.FC = () => {
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showFloatingWidget, setShowFloatingWidget] = useState(true);
  const [hasInteractedWithMainChat, setHasInteractedWithMainChat] =
    useState(false);
  const [lastAgenticNotice, setLastAgenticNotice] = useState<string | null>(
    null
  );

  // Agentic authentication state
  const [agenticAuthState, setAgenticAuthState] = useState({
    showModal: false,
    loading: false,
    error: "",
    code: "manifest-123456",
    lastAttempt: null as Date | null,
    isVerified: false,
  });

  // Track current session for auth purposes
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  useEffect(() => {
    const handleNotification = (event: any) => {
      const { type, title, message } = event.detail;
      // You can use a toast library or simple alert
      // Or use your existing notification system
    };

    window.addEventListener("showNotification", handleNotification);
    return () => {
      window.removeEventListener("showNotification", handleNotification);
    };
  }, []);

  // Check initial verification status
  useEffect(() => {
    const isVerified = sessionManager.isAgenticVerified();
    setAgenticAuthState((prev) => ({ ...prev, isVerified }));
  }, []);

  // Listen for chat events and agentic notices
  useEffect(() => {
    const handleChatInteraction = () => {
      setHasInteractedWithMainChat(true);
      setShowFloatingWidget(false);
    };

    const handleAgenticNotice = (event: any) => {
      if (event.detail?.intent || event.detail?.fallback) {
        const intentInfo = event.detail.intent;

        // Set the action description for UI display
        if (intentInfo) {
          setLastAgenticNotice(`${intentInfo.action} (${intentInfo.topic})`);
        }

        setShowLoginPrompt(true);

        setAgenticAuthState((prev) => ({
          ...prev,
          showModal: true,
          error: "",
          code: "manifest-123456", // Keep default code
          // Store the pending action details for potential retry
          pendingSessionId: event.detail.sessionId || currentSessionId,
          pendingMessageId: event.detail.messageId,
        }));
      } else {
      }
    };

    const handleSessionCreated = (event: any) => {
      if (event.detail?.sessionId) {
        setCurrentSessionId(event.detail.sessionId);
      }
    };

    const handleNotification = (event: any) => {
      const { type, title, message } = event.detail;

      // You can integrate with your existing notification system here
      // For example, if you have a toast notification library:
      // toast[type](title, message);
    };

    // Add all event listeners
    window.addEventListener("sendPublicMessage", handleChatInteraction);
    window.addEventListener("agenticNotice", handleAgenticNotice);
    window.addEventListener("publicChatSessionCreated", handleSessionCreated);
    window.addEventListener("showNotification", handleNotification);

    return () => {
      window.removeEventListener("sendPublicMessage", handleChatInteraction);
      window.removeEventListener("agenticNotice", handleAgenticNotice);
      window.removeEventListener(
        "publicChatSessionCreated",
        handleSessionCreated
      );
      window.removeEventListener("showNotification", handleNotification);
    };
  }, [currentSessionId]); // Keep currentSessionId as dependency

  // Handle agentic verification
  const handleAgenticVerification = async () => {
    if (!agenticAuthState.code.trim()) {
      setAgenticAuthState((prev) => ({
        ...prev,
        error: "Please enter your access code",
      }));
      return;
    }

    setAgenticAuthState((prev) => ({
      ...prev,
      loading: true,
      error: "",
      lastAttempt: new Date(),
    }));

    try {
      // Use the public chat auth endpoint if we have a session
      if (currentSessionId) {
        const response = await publicChatApi.authenticateSession(
          currentSessionId,
          {
            accessCode: agenticAuthState.code.trim(),
          }
        );

        if (response.data.success) {
          // Store verification status
          sessionManager.setAgenticVerified(response.data.principalId);

          // Update UI state
          setAgenticAuthState({
            showModal: false,
            loading: false,
            error: "",
            code: "manifest-123456",
            lastAttempt: null,
            isVerified: true,
          });

          // Clear login prompt
          setShowLoginPrompt(false);
          setLastAgenticNotice(null);

          // Show success notification
          const successEvent = new CustomEvent("showNotification", {
            detail: {
              type: "success",
              title: "Authentication Successful",
              message: "Access granted. Your action has been processed.",
            },
          });
          window.dispatchEvent(successEvent);

          // ✅ ENHANCED: Check if server provided replay data for automatic execution
          if (response.data.replay_response) {
            // ✅ ADD: Small delay to ensure component is ready
            setTimeout(() => {
              // Dispatch the replay event so PublicChat component can handle it
              const replayEvent = new CustomEvent("agenticActionReplayed", {
                detail: { response: response.data.replay_response },
              });
              window.dispatchEvent(replayEvent);

              // Update success message to reflect automatic execution
              const executedEvent = new CustomEvent("showNotification", {
                detail: {
                  type: "success",
                  title: "Action Executed",
                  message: "Your request has been processed automatically.",
                },
              });
              window.dispatchEvent(executedEvent);
            }, 100);
          } else {
          }
        } else {
          setAgenticAuthState((prev) => ({
            ...prev,
            loading: false,
            error:
              response.data.message || "Invalid access code. Please try again by calling the previous action and then enter a valid verification code.",
          }));
        }
      } else {
        // Fallback to general agentic auth if no session
        const response = await agenticAuthApi.verify({
          accessKey: agenticAuthState.code.trim(),
        });

        if (response.data.ok) {
          sessionManager.setAgenticVerified(response.data.principalId);

          setAgenticAuthState({
            showModal: false,
            loading: false,
            error: "",
            code: "manifest-123456",
            lastAttempt: null,
            isVerified: true,
          });

          setShowLoginPrompt(false);
          setLastAgenticNotice(null);

          // ✅ ADD: Check for replay data from general auth too
          if (response.data.replay) {
            setTimeout(() => {
              const replayEvent = new CustomEvent("agenticActionReplayed", {
                detail: { response: response.data.replay },
              });
              window.dispatchEvent(replayEvent);
            }, 100);
          }
        } else {
          setAgenticAuthState((prev) => ({
            ...prev,
            loading: false,
            error: "Invalid access code. Please try again by calling the previous action and then enter a valid verification code.",
          }));
        }
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Verification failed. Please try again.";

      setAgenticAuthState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  };

  return (
    <div className="min-h-screen bg-transparent flex items-center justify-center">
      {/* Main Content */}
      <div className=" !mx-auto px-4 sm:px-6 lg:px-8 py-8 ">
        <div
        // className={`grid gap-8 transition-all duration-300  ${
        //   sidebarCollapsed ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-3"
        // }`}
        >
          {/* Chat Interface - Responsive width based on sidebar state */}
          <div
            className={`transition-all duration-300 w-[780px] ${
              sidebarCollapsed
                ? "col-span-1 max-w-4xl mx-auto"
                : "lg:col-span-2"
            }`}
          >
            <div className="rounded-xl shadow-lg overflow-hidden">
              {/* Welcome Section */}
              <div className="bg-[#8b3dff] text-white p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Bot className="w-6 h-6" />
                  </div>
                  {/* <h2 className="text-2xl font-bold mb-2">
                    Welcome to Pridict Support
                  </h2>
                  <p className="text-purple-100 mb-4">
                    Get instant help with our AI-powered customer service. Ask
                    questions about our services, get technical support, or
                    learn more about our products.
                  </p> */}
                  <div className="">
                    <h3 className="18px font-semibold">Customer Support</h3>
                    {/* <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span>AI-Powered</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                      <span>Instant Responses</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-pink-400 rounded-full"></div>
                      <span>24/7 Available</span>
                    </div> */}
                    {agenticAuthState.isVerified && (
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-300 rounded-full"></div>
                        <span className="text-[14px]">Enhanced Access</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Chat Interface */}
              <div className="relative">
                <PublicChat
                  isEmbedded={false}
                  className="h-full rounded-none border-none"
                />

                {/* Enhanced Login Prompt Overlay */}
                {showLoginPrompt && (
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-yellow-900/80 to-orange-900/80 border border-yellow-500 rounded-lg p-4 shadow-lg max-w-sm">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-4 h-4 text-yellow-400" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-white mb-1">
                          Authentication Required
                        </h4>
                        <p className="text-xs text-gray-300 mb-3">
                          {lastAgenticNotice
                            ? `Action "${lastAgenticNotice}" requires authentication.`
                            : "This action requires special permissions."}
                        </p>
                        <div className="flex space-x-2">
                          <button
                            onClick={() =>
                              setAgenticAuthState((prev) => ({
                                ...prev,
                                showModal: true,
                              }))
                            }
                            className="text-xs px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors flex items-center space-x-1"
                          >
                            <Key className="w-3 h-3" />
                            <span>Enter Code</span>
                          </button>
                          <button
                            onClick={() => setShowLoginPrompt(false)}
                            className="text-xs px-3 py-1 text-gray-300 hover:text-white"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              {/* <div className="bg-[#020617] p-4 border-t border-[#334155]">
                <div className="flex flex-col sm:flex-row items-center justify-between text-sm text-[#64748b] space-y-2 sm:space-y-0">
                  <div className="flex items-center space-x-4">
                    <span className="flex items-center space-x-1 text-[10px]">
                      <Shield className="w-4 h-4" />
                      <span>Secure & Private</span>
                    </span>
                    <span className="flex items-center space-x-1 text-[10px]">
                      <Zap className="w-4 h-4" />
                      <span>AI-Powered</span>
                    </span>
                    <span className="flex items-center space-x-1 text-[10px]">
                      <BookOpen className="w-4 h-4" />
                      <span>Knowledge Base Enabled</span>
                    </span>
                    {agenticAuthState.isVerified && (
                      <span className="flex items-center space-x-1 text-[10px] text-green-400">
                        <Lock className="w-4 h-4" />
                        <span>Enhanced Features</span>
                      </span>
                    )}
                  </div>
                  <div className="text-center sm:text-right text-[10px]">
                    <p>
                      Need more help?{" "}
                      <a
                        href="mailto:support@pridict.com"
                        className="text-[#8b3dff] hover:text-purple-800"
                      >
                        Contact us directly
                      </a>
                    </p>
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </div>

        {/* Floating Sidebar Toggle for Mobile when collapsed */}
        {/* {sidebarCollapsed && (
          <button
            onClick={() => setSidebarCollapsed(false)}
            className="fixed bottom-20 right-4 lg:hidden w-12 h-12 bg-[#1e293b] border border-[#334155] rounded-full shadow-lg flex items-center justify-center text-[#64748b] hover:text-gray-800 hover:shadow-xl transition-all z-40"
            title="Show help panel"
          >
            <Menu className="w-5 h-5" />
          </button>
        )} */}

        {/* Features Grid - Only show when sidebar is visible or on mobile */}
      </div>

      {/* Floating Chat Widget */}
      {/* {showFloatingWidget && (
        <PublicChatWidget
          position="bottom-right"
          theme="purple"
          customTitle="Quick Support Chat"
        />
      )} */}

      {/* Agentic Authentication Modal */}
      {agenticAuthState.showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-[#1e293b] rounded-lg shadow-xl p-6 w-full max-w-md mx-4 border border-[#334155]">
            <div className="flex items-start space-x-3 mb-4">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Key className="w-5 h-5 text-yellow-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-white">
                  Authentication Required
                </h2>
                <p className="text-sm text-gray-300 mt-1">
                  {lastAgenticNotice
                    ? `To perform "${lastAgenticNotice}", please enter your access code.`
                    : "This action requires special permissions. Please enter your access code to continue."}
                </p>
              </div>
            </div>

            {agenticAuthState.error && (
              <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-md">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <p className="text-sm text-red-300">
                    {agenticAuthState.error}
                  </p>
                </div>
              </div>
            )}

            {agenticAuthState.lastAttempt && (
              <div className="mb-4 p-3 bg-blue-900/50 border border-blue-500 rounded-md">
                <p className="text-sm text-blue-300">
                  Last attempt:{" "}
                  {agenticAuthState.lastAttempt.toLocaleTimeString()}
                </p>
              </div>
            )}

            <div className="mb-4">
              <label
                htmlFor="accessCode"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Access Code
              </label>
              <input
                id="accessCode"
                type="text"
                value={agenticAuthState.code}
                onChange={(e) =>
                  setAgenticAuthState((prev) => ({
                    ...prev,
                    code: e.target.value,
                    error: "",
                  }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !agenticAuthState.loading) {
                    handleAgenticVerification();
                  }
                  if (e.key === "Escape") {
                    setAgenticAuthState((prev) => ({
                      ...prev,
                      showModal: false,
                    }));
                  }
                }}
                className="w-full bg-[#020617] border border-[#334155] rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-white placeholder-gray-400"
                defaultValue="manifest-123456"
                placeholder="Enter your access code"
                disabled={agenticAuthState.loading}
                autoFocus
              />
              <p className="text-xs text-gray-400 mt-1">
                This code grants enhanced access for 24 hours.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() =>
                  setAgenticAuthState((prev) => ({
                    ...prev,
                    showModal: false,
                    code: "manifest-123456",
                    error: "",
                  }))
                }
                disabled={agenticAuthState.loading}
                className="px-4 py-2 text-gray-300 hover:text-white disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAgenticVerification}
                disabled={
                  agenticAuthState.loading || !agenticAuthState.code.trim()
                }
                className="px-4 py-2 bg-[#8b3dff] text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2 transition-colors"
              >
                {agenticAuthState.loading && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <Key className="w-4 h-4" />
                <span>
                  {agenticAuthState.loading ? "Verifying..." : "Verify Access"}
                </span>
              </button>
            </div>

            <div className="mt-4 pt-4 border-t border-[#334155] text-center">
              <p className="text-xs text-gray-400">
                Having trouble? Contact support at{" "}
                <a
                  href="mailto:support@pridict.com"
                  className="text-[#8b3dff] hover:text-purple-800"
                >
                  support@pridict.com
                </a>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* <button
        onClick={() => {
          sessionManager.clearSession();
          window.location.reload();
        }}
        className="fixed top-16 left-4 bg-orange-500 text-white p-2 rounded z-50 text-xs"
      >
        Clear Auth & Reload
      </button> */}

      {/* {process.env.NODE_ENV === "development" && (
        <button
          onClick={() => {


            // Test fire event manually
            const testEvent = new CustomEvent("agenticNotice", {
              detail: {
                intent: {
                  type: "AGENTIC_ACTION",
                  action: "TEST",
                  topic: "DEBUG",
                },
                messageId: "test-123",
                sessionId: currentSessionId,
              },
            });
            window.dispatchEvent(testEvent);
          }}
          className="fixed top-24 left-4 bg-blue-500 text-white p-2 rounded z-50 text-xs"
        >
          Debug Modal
        </button>
      )} */}
    </div>
  );
};

export default WidgetChat;